import React, { useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import './StarBackground.css';

type PerformanceLevel = 'high' | 'medium' | 'low';

const StarBackground: React.FC = () => {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const shootingStarsRef = useRef<HTMLDivElement>(null);
  const isVisibleRef = useRef<boolean>(true);
  const performanceLevelRef = useRef<PerformanceLevel>('medium');

  // 성능 레벨 감지
  const detectPerformanceLevel = useCallback((): PerformanceLevel => {
    const cores = navigator.hardwareConcurrency || 4;
    if (cores >= 8) return 'high';
    if (cores >= 4) return 'medium';
    return 'low';
  }, []);

  // 별똥별 생성 (성능 최적화)
  const createShootingStars = useCallback(() => {
    const container = shootingStarsRef.current;
    if (!container) return;

    const createMultipleStars = () => {
      if (!isVisibleRef.current) return;

      // 성능 레벨에 따른 별똥별 개수 조절
      const maxStars = performanceLevelRef.current === 'high' ? 3 : performanceLevelRef.current === 'medium' ? 2 : 1;
      const starCount = Math.floor(Math.random() * maxStars) + 1;

      for (let i = 0; i < starCount; i++) {
        setTimeout(() => {
          if (!isVisibleRef.current) return;

          const star = document.createElement('div');
          star.className = 'shooting-star';
          star.style.top = `${Math.random() * 70}%`;
          star.style.left = `${90 + Math.random() * 20}%`;
          const duration = 1.5 + Math.random() * 2;
          star.style.animationDuration = `${duration}s`;
          star.style.animationDelay = `${Math.random() * 0.5}s`;
          container.appendChild(star);

          setTimeout(
            () => {
              if (star.parentNode) {
                star.remove();
              }
            },
            (duration + 1) * 1000
          );
        }, i * 200);
      }
    };

    createMultipleStars();

    // 성능 레벨에 따른 인터벌 조절
    const interval1 = setInterval(createMultipleStars, performanceLevelRef.current === 'high' ? 800 : 1200);
    const interval2 = setInterval(
      () => {
        if (Math.random() > 0.3 && isVisibleRef.current) createMultipleStars();
      },
      performanceLevelRef.current === 'high' ? 500 : 800
    );

    return () => {
      clearInterval(interval1);
      clearInterval(interval2);
    };
  }, []);

  // 별 생성
  const generateStars = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const starsContainer = container.querySelector('.stars');
    if (!starsContainer) return;

    // 기존 별 제거
    starsContainer.innerHTML = '';

    // 별 개수 계산
    const width = window.innerWidth;
    const height = window.innerHeight;
    const starCount = Math.floor((width * height) / 15000);

    for (let i = 0; i < starCount; i++) {
      const star = document.createElement('div');
      star.className = 'star';
      
      const isBrightStar = Math.random() < 0.3; // 30%는 밝은 별
      const size = isBrightStar 
        ? Math.random() * 3 + 2  // 밝은 별: 2-5px
        : Math.random() * 2 + 1; // 일반 별: 1-3px
      
      star.style.width = `${size}px`;
      star.style.height = `${size}px`;
      star.style.left = `${Math.random() * 100}%`;
      star.style.top = `${Math.random() * 100}%`;
      star.style.animationDelay = `${Math.random() * 3}s`;
      star.style.animationDuration = `${2 + Math.random() * 2}s`;
      
      if (isBrightStar) {
        star.classList.add('bright-star');
      }
      
      starsContainer.appendChild(star);
    }
  }, []);

  useEffect(() => {
    if (theme !== 'dark') return;

    // 성능 레벨 감지
    performanceLevelRef.current = detectPerformanceLevel();

    // 가시성 감지
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 별 생성
    generateStars();

    // 리사이즈 시 별 재생성
    const handleResize = () => {
      generateStars();
    };
    window.addEventListener('resize', handleResize);

    // 별똥별 생성 시작
    const cleanup = createShootingStars();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('resize', handleResize);
      if (cleanup) cleanup();
    };
  }, [theme, detectPerformanceLevel, createShootingStars, generateStars]);

  if (theme !== 'dark') return null;

  return (
    <div ref={containerRef} className="star-background">
      <div className="stars"></div>
      <div ref={shootingStarsRef} className="shooting-stars-container"></div>
    </div>
  );
};

export default StarBackground;
